import { AxiosResponse } from 'axios';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PhoneValidationContainerInModal } from '@/components/Containers/PhoneValidationContainer';
import { ProductsContainer } from '@/components/Containers/ProductsContainer';
import { ConfirmNeededPaymentModal, Spinner } from '@/components/Elements';
import { ActivationNeededModal } from '@/components/Elements/ActivationNeededModal';
import { ErrorModal } from '@/components/Elements/ErrorModal';
import { FeatureComparatorLink } from '@/components/Elements/FeatureComparatorLink';
import { IncompletePaymentMethodFailedErrorModal } from '@/components/Elements/IncompletePaymentMethodFailedErrorModal';
import { IncompletePaymentRequiresActionErrorModal } from '@/components/Elements/IncompletePaymentRequiresActionErrorModal';
import { tinyModalStyles } from '@/components/Elements/Modal/customStyles';
import { PaymentErrorModal } from '@/components/Elements/PaymentErrorModal';
import { PaymentsModal } from '@/components/Elements/PaymentsModal';
import { PendingPaymentErrorModal } from '@/components/Elements/PendingPaymentErrorModal';
import { SummaryPaymentModal } from '@/components/Elements/SummaryPaymentModal/SummaryPaymentModal';
import { TourButtonRequest } from '@/components/Elements/TourButtonRequest';
import { UnpaidMeteredErrorModal } from '@/components/Elements/UnpaidMeteredErrorModal';
import { WaitProcessModal } from '@/components/Elements/WaitProcessModal';
import { iFormContactFields } from '@/components/Form/FormContact';
import { FormContactInModal } from '@/components/Form/FormContactInModal';
import { iFormPhoneValidationFields } from '@/components/Form/FormPhoneValidation';
import { iFormTokenCheckFields } from '@/components/Form/FormTokenCheck';
import { iFormUserAccountFields } from '@/components/Form/FormUserAccount';
import { FormUserAccountInModal } from '@/components/Form/FormUserAccountInModal';
import {
  COUPONS_ENABLED,
  PATH_UPDATE_PAYMENT_METHOD,
  SHOW_TOUR_BUTTON_ON_PRODUCTS,
  TOUR_BUTTON_REQUEST_URL,
} from '@/config';
import { ACTIVE, DEFAULT_COUNTRY_ID, DEFAULT_PHONE, REQUEST_SUCCESS } from '@/config/appConstants';
import { BillingDetails } from '@/features/billing';
import { Country } from '@/features/countries';
import { MobileToken } from '@/features/mobileToken';
import { PaymentIntent, UpdatePaymentIntent } from '@/features/payment';
import { Province } from '@/features/provinces';
import { SmsCredits } from '@/features/smsCredits';
import { TinDocumentType } from '@/features/tinDocumentTypes';
import { useMe } from '@/hooks/useMe';
import { useUserProducts } from '@/hooks/useUserProducts';
import {
  Currency,
  IntervalBillingMode,
  UserData,
  ProductCategoryTypes,
  ProductTypes,
  UserProductTypes,
} from '@/types';
import {
  getOrigin,
  checkHasToShowPhoneValidationForm,
  checkHasToShowUserAccountForm,
  filterUserProductsByStatus,
  isCoreProduct,
  isManualValidationNeeded,
  userHasProducts,
  isFreemiumProduct,
  isPrepaidProduct,
  isEmailMarketingTab,
  getProductToUpdateFromUserCollection,
  isAppSumo,
  isNewFreeUserFromMe,
  getPromotionCodeByCurrencyAndInterval,
  redirectToAndReload,
} from '@/utils/helpers';

export type ProductsUpdatePrivatePageProps = {
  onRequestInfoSubmit: (values: iFormContactFields) => void;
  userData: UserData;
  userCurrency: Currency;
  products: ProductTypes[];
  userProducts: UserProductTypes[] | undefined;
  freemiumProduct: ProductTypes;
  billingDetails?: BillingDetails;
  countries: Country[];
  provinces: Province[];
  tinDocumentTypes: TinDocumentType[];
  countryOnChange: (formValues: any, countryId: string) => void;
  getCreatePaymentIntent?: (
    productSelected: ProductTypes | null | undefined,
    userCurrency: Currency,
    intervalCount: IntervalBillingMode,
    preview: boolean,
    promotionCode: string | '',
    onErrorCallback: (status: number) => void
  ) => Promise<PaymentIntent | undefined>;

  getUpdatePaymentIntent?: (
    productSelected: ProductTypes,
    userCurrency: Currency,
    intervalCount: IntervalBillingMode,
    promotionCode: string | '',
    onErrorCallback: (status: number, urlRedirect?: string, detail?: string) => void
  ) => Promise<UpdatePaymentIntent | AxiosResponse | void>;

  onCancelButton: (product: ProductTypes | null | undefined) => void;
  onCancelIncompleteAction: () => void;
  onSubmitFormUserAccount: (values: iFormUserAccountFields) => Promise<boolean>;
  onProductSelect?: (product: ProductTypes) => void;
  onFreeProductSelect?: (product: ProductTypes) => void;
  onFinishURLRedirect?: string;
  demoMode?: boolean;
  smsCredits: SmsCredits;
  mobileToken: MobileToken;
  onSubmitFormPhoneValidation: (values: iFormPhoneValidationFields) => void;
  onSubmitFormTokenCheck: (values: iFormTokenCheckFields) => void;
  onConfirmUnpaidMeteredErrorAction: () => void;
};

export const ProductsUpdatePrivatePage = ({
  onRequestInfoSubmit,
  userData,
  userCurrency,
  products,
  freemiumProduct,
  userProducts,
  countries,
  provinces,
  tinDocumentTypes,
  countryOnChange,
  billingDetails,
  getCreatePaymentIntent,
  getUpdatePaymentIntent,
  onCancelButton,
  onCancelIncompleteAction,
  onSubmitFormUserAccount,
  onProductSelect,
  onFreeProductSelect,
  onFinishURLRedirect,
  smsCredits,
  mobileToken,
  onSubmitFormPhoneValidation,
  onSubmitFormTokenCheck,
  demoMode = false,
  onConfirmUnpaidMeteredErrorAction,
}: ProductsUpdatePrivatePageProps) => {
  const NO_CLOSE = 0;
  const REQUEST_PREVIEW = true;
  const { setSelectedProduct, getSelectedUserProduct } = useUserProducts();
  const {
    selectedProduct,
    selectedProductBillingMode,
    selectedProductIntervalBillingMode,
    selectedProductCurrency,
  } = getSelectedUserProduct();
  const { getMe } = useMe();

  const userProductActive = filterUserProductsByStatus(userProducts || [], ACTIVE);
  const [tabSelected, setTabSelected] = useState<ProductCategoryTypes>('emailMarketing');
  const [requestInfoModalIsOpen, setRequestInfoModalIsOpen] = useState(false);
  const [confirmNeededPaymentModalIsOpen, setConfirmNeededPaymentModalIsOpen] = useState(false);
  const [requestUserAccountModalIsOpen, setRequestUserAccountModalIsOpen] = useState(false);
  const [validationNeededModalIsOpen, setValidationNeededModalIsOpen] = useState(false);
  const [requestPhoneValidationModalIsOpen, setRequestPhoneValidationModalIsOpen] = useState(false);
  const [summaryPaymentModalIsOpen, setSummaryPaymentModalIsOpen] = useState(false);
  const [invoiceSummaryData, setInvoiceSummaryData] = useState<any | null>(null);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [showPaymentError, setShowPaymentError] = useState<boolean>(false);
  const [statusPaymentError, setStatusPaymentError] = useState<number>(0);
  const [
    showIncompletePaymentRequiresActionErrorModal,
    setShowIncompletePaymentRequiresActionErrorModal,
  ] = useState<boolean | string>(false);
  const [
    showIncompletePaymentMethodFailedErrorModal,
    setShowIncompletePaymentMethodFailedErrorModal,
  ] = useState<boolean | string>(false);

  const [statusUnpaidMeteredError, setStatusUnpaidMeteredError] = useState(false);
  const [statusPendingPaymentError, setStatusPendingPaymentError] = useState(false);

  const [userCurrencySelected, setUserCurrencySelected] = useState<Currency>(userCurrency);
  const [upgradeCheckoutProcessModalIsOpen, setUpgradeCheckoutProcessModalIsOpen] = useState(false);
  const [upgradeCheckoutSuccessProcessModalIsOpen, setUpgradeCheckoutSuccessProcessModalIsOpen] =
    useState(false);
  const [upgradeCheckoutFailProcessModalIsOpen, setUpgradeCheckoutFailProcessModalIsOpen] =
    useState(false);
  const [origin, setOrigin] = useState<string | null>(null);
  const { name, groupId, email } = userData;
  const countryId = billingDetails?.countryId || DEFAULT_COUNTRY_ID;
  const phone = billingDetails?.phone || DEFAULT_PHONE;

  const { t } = useTranslation();
  const onDefaultProductSelect = (product: ProductTypes) => {
    const userPromotionCode = '';
    setSelectedProduct(product);
    onProductSelect && onProductSelect(product);

    setUserCurrencySelected(selectedProductCurrency);

    if (checkHasToShowUserAccountForm(selectedProductCurrency, getMe(), billingDetails)) {
      if (checkHasToShowPhoneValidationForm(mobileToken, getMe())) {
        onRequestPhoneValidationModalOpen();
        return;
      }

      onRequestUserAccountModalOpen();
      return;
    }

    if (isManualValidationNeeded(getMe()) || isNewFreeUserFromMe(getMe())) {
      onShowValidationNeededModalOpen();
      return;
    }

    onRequestPaymentModalOpen(selectedProductCurrency, product, userPromotionCode);
  };

  const onDefaultFreeProductSelect = (product: ProductTypes) => {
    onRequestInfo(product);
    onFreeProductSelect && onFreeProductSelect(product);
    setUserCurrencySelected(selectedProductCurrency);
  };

  const closeAllModals = (omit?: string) => {
    omit !== 'InfoModal' && setRequestInfoModalIsOpen(false);
    omit !== 'UserAccountModal' && setRequestUserAccountModalIsOpen(false);
    omit !== 'PhoneValidationModal' && setRequestPhoneValidationModalIsOpen(false);
    omit !== 'ValidationNeededModal' && setValidationNeededModalIsOpen(false);
    omit !== 'ConfirmNeededPaymentModal' && setConfirmNeededPaymentModalIsOpen(false);
    omit !== 'SummaryPaymentModal' && setSummaryPaymentModalIsOpen(false);

    if (omit !== 'WaitProcessModal') {
      setUpgradeCheckoutProcessModalIsOpen(false);
      setUpgradeCheckoutSuccessProcessModalIsOpen(false);
    }

    // This modal can not be omitted
    setUpgradeCheckoutFailProcessModalIsOpen(false);
  };

  const onRequestInfoClose = () => {
    closeAllModals();
  };

  const onRequestInfo = (product: ProductTypes | null, origin: string | null = null) => {
    setOrigin(origin);
    setSelectedProduct(product || null);
    setRequestInfoModalIsOpen(true);
    closeAllModals('InfoModal');
  };

  const onDefaultRequestInfoSubmit = (values: iFormContactFields) => {
    onRequestInfoSubmit(values);
    onRequestInfoClose();
  };

  const onRequestPaymentModalClose = () => {
    closeAllModals();
  };

  const onRequestCloseActivationNeededModal = () => {
    closeAllModals();
  };

  const onRequestCloseConfirmNeededPaymentModal = () => {
    closeAllModals();
    setStatusUnpaidMeteredError(false);
    setStatusPendingPaymentError(false);
  };

  const onUpgradeCheckoutErrorCallback = (
    status: number,
    urlRedirect?: string,
    detail?: string
  ) => {
    closeAllModals();

    if (status === 402 && detail == 'requires_action')
      return setShowIncompletePaymentRequiresActionErrorModal(
        urlRedirect || PATH_UPDATE_PAYMENT_METHOD
      );
    if (status === 402 && detail == 'payment_method_failed')
      return setShowIncompletePaymentMethodFailedErrorModal(
        urlRedirect || PATH_UPDATE_PAYMENT_METHOD
      );

    if (
      status === 409 &&
      detail &&
      detail === 'There are unpaid metered items in this subscription'
    )
      return setStatusUnpaidMeteredError(status > 0);

    if (
      status === 409 &&
      detail &&
      detail === 'There are pending payments for the current subscription'
    )
      return setStatusPendingPaymentError(status > 0);
    if (
      status === 409 &&
      detail &&
      detail === 'Cannot update a subscription with an incomplete payment'
    )
      return setStatusPaymentError(status);

    setUpgradeCheckoutFailProcessModalIsOpen(true);
  };

  const onUpgradeCheckoutModalClose = () => {
    closeAllModals();
  };

  const onActionConfirmNeededPaymentModal = async (promotionCode: string | '') => {
    if (
      !getUpdatePaymentIntent ||
      !selectedProduct ||
      !selectedProductCurrency ||
      !selectedProductIntervalBillingMode
    ) {
      return;
    }

    setUpgradeCheckoutProcessModalIsOpen(true);
    if (!promotionCode)
      promotionCode = getPromotionCodeByCurrencyAndInterval(
        selectedProduct.prices,
        selectedProductCurrency,
        selectedProductIntervalBillingMode
      );

    const updatePaymentResponse = await getUpdatePaymentIntent(
      selectedProduct,
      selectedProductCurrency,
      selectedProductIntervalBillingMode,
      promotionCode,
      onUpgradeCheckoutErrorCallback
    );

    if (updatePaymentResponse?.status === REQUEST_SUCCESS) {
      setUpgradeCheckoutProcessModalIsOpen(false);
      setUpgradeCheckoutSuccessProcessModalIsOpen(true);
    }
  };

  const onRequestPaymentModalOpen = async (
    currency: Currency,
    product: ProductTypes | null,
    promotionCode: string | ''
  ) => {
    // We print the "UpdateProduct" modal always user has a product non freemium.
    if (
      userProducts &&
      userProducts.length > 0 &&
      !isFreemiumProduct(userProductActive?.product) &&
      !isPrepaidProduct(product)
    ) {
      setConfirmNeededPaymentModalIsOpen(true);
    } else {
      const invoiceSummaryData = await generatePaymentIntent(
        currency,
        product,
        selectedProductIntervalBillingMode,
        REQUEST_PREVIEW,
        promotionCode
      );

      setInvoiceSummaryData(invoiceSummaryData);

      if (invoiceSummaryData) {
        setSummaryPaymentModalIsOpen(true);
      }

      closeAllModals('SummaryPaymentModal');
    }
  };

  const onShowValidationNeededModalOpen = () => {
    setValidationNeededModalIsOpen(true);
    closeAllModals('ValidationNeededModal');
  };

  const onRequestUserAccountModalClose = () => {
    closeAllModals();
  };

  const onRequestUserAccountModalOpen = () => {
    setRequestUserAccountModalIsOpen(true);
    closeAllModals('UserAccountModal');
  };

  const onDefaultFormUserAccountSubmit = async (values: iFormUserAccountFields) => {
    const userPromotionCode = '';
    const update = await onSubmitFormUserAccount(values);
    if (!update) {
      return false;
    }

    onRequestUserAccountModalClose();
    isManualValidationNeeded(getMe()) || isNewFreeUserFromMe(getMe())
      ? onShowValidationNeededModalOpen()
      : onRequestPaymentModalOpen(userCurrencySelected, selectedProduct, userPromotionCode);

    return true;
  };

  const onRequestPhoneValidationModalOpen = () => {
    setRequestPhoneValidationModalIsOpen(true);
    closeAllModals('PhoneValidationModal');
  };

  const onRequestPhoneValidationModalClose = () => {
    closeAllModals();
  };

  const onErrorCallback = (status: number) => {
    closeAllModals();
    if (status === 503 || status === 500 || status === 402) return setStatusPaymentError(status);
    setShowPaymentError(true);
  };

  const hasToRedirectStripeCheckOut = (url: string) => {
    window.location.assign(url);
  };

  const generatePaymentIntent = async (
    currency: Currency,
    product: ProductTypes | null,
    intervalCount: IntervalBillingMode,
    preview = false,
    promotionCode: string | ''
  ) => {
    if (
      getCreatePaymentIntent &&
      product &&
      (userHasProducts(userProducts) || !isCoreProduct(product))
    ) {
      setShowSpinner(true);

      const paymentIntent = await getCreatePaymentIntent(
        product,
        currency,
        intervalCount,
        preview,
        promotionCode,
        onErrorCallback
      );

      setShowSpinner(false);

      if (paymentIntent) {
        if (preview) {
          return paymentIntent;
        }

        if (paymentIntent.sessionUrl) {
          hasToRedirectStripeCheckOut(paymentIntent.sessionUrl);
        }
      }

      setShowSpinner(false);
    }
  };

  const onDefaultCancelButton = async (product: ProductTypes | null | undefined) => {
    onCancelButton(product);
    onRequestInfoClose();
  };

  const onDefaultSubmitFormTokenCheck = (values: iFormTokenCheckFields) => {
    onSubmitFormTokenCheck && onSubmitFormTokenCheck(values);
    setRequestUserAccountModalIsOpen(true);
  };

  const onDefaultConfirmIncompleteAction = () => {
    redirectToAndReload(showIncompletePaymentRequiresActionErrorModal as string);
    setShowIncompletePaymentRequiresActionErrorModal(false);
    setShowIncompletePaymentMethodFailedErrorModal(false);
  };

  const onDefaultCancelIncompleteAction = async () => {
    onCancelIncompleteAction && (await onCancelIncompleteAction());
    setShowIncompletePaymentRequiresActionErrorModal(false);
    setShowIncompletePaymentMethodFailedErrorModal(false);
  };

  const onDefaultConfirmPendingPaymentErrorAction = () => {
    window.location.assign(PATH_UPDATE_PAYMENT_METHOD);
    onRequestCloseConfirmNeededPaymentModal && onRequestCloseConfirmNeededPaymentModal();
  };

  const onDefaultConfirmUnpaidMeteredErrorAction = () => {
    onConfirmUnpaidMeteredErrorAction && onConfirmUnpaidMeteredErrorAction();

    onRequestCloseConfirmNeededPaymentModal && onRequestCloseConfirmNeededPaymentModal();
  };

  const renderSpinner = (): JSX.Element | null => {
    if (showSpinner) {
      return (
        <PaymentsModal
          closeButton={false}
          isOpen={showSpinner}
          style={tinyModalStyles}
          shouldCloseOnOverlayClick={false}
        >
          <div className="flex items-center justify-center">
            <Spinner size="xl" />
          </div>
        </PaymentsModal>
      );
    }

    return null;
  };

  const renderPaymentError = () => (
    <PaymentErrorModal
      titleTag="PaymentForm.paymentFailTitle"
      isOpen={showPaymentError}
      onRequestClose={() => {
        setShowPaymentError(false);
      }}
      shouldRedirect={true}
    />
  );

  const renderIncompletePaymentRequiresActionErrorModal = () => (
    <IncompletePaymentRequiresActionErrorModal
      isOpen={showIncompletePaymentRequiresActionErrorModal != false}
      onConfirmAction={onDefaultConfirmIncompleteAction}
      onCancelAction={onDefaultCancelIncompleteAction}
    />
  );

  const renderIncompletePaymentMethodFailedErrorModal = () => (
    <IncompletePaymentMethodFailedErrorModal
      isOpen={showIncompletePaymentMethodFailedErrorModal != false}
      onConfirmAction={onDefaultConfirmIncompleteAction}
      onCancelAction={onDefaultCancelIncompleteAction}
    />
  );

  const renderPaymentUpdateError = () => (
    <PaymentErrorModal
      titleTag="PaymentForm.upgradeFailTitle"
      isOpen={upgradeCheckoutFailProcessModalIsOpen}
      onRequestClose={onUpgradeCheckoutModalClose}
    />
  );

  const renderStatusUnpaidMeteredErrorModal = () => (
    <UnpaidMeteredErrorModal
      isOpen={statusUnpaidMeteredError != false}
      onConfirmAction={onDefaultConfirmUnpaidMeteredErrorAction}
      onCancelAction={onRequestCloseConfirmNeededPaymentModal}
    />
  );

  const renderStatusPendingPaymentErrorModal = () => (
    <PendingPaymentErrorModal
      isOpen={statusPendingPaymentError != false}
      onConfirmAction={onDefaultConfirmPendingPaymentErrorAction}
      onCancelAction={onRequestCloseConfirmNeededPaymentModal}
    />
  );

  const timeoutCheckoutSuccessWaitProcess = () => {
    onFinishURLRedirect && window.location.assign(onFinishURLRedirect);
  };

  const userIsLogged = () => {
    if (demoMode || groupId) {
      return true;
    }

    return false;
  };

  const onComparatorLinkClick = () => {
    window.location.assign('/account/feature-comparator');
  };

  const phoneValidationNeeded: boolean = checkHasToShowPhoneValidationForm(mobileToken, getMe());
  const oldUserProduct = getProductToUpdateFromUserCollection(userProductActive, userProducts);

  const onActionConfirmSummaryPaymentModal = async (promotionCode: string) => {
    await generatePaymentIntent(
      selectedProductCurrency,
      selectedProduct,
      selectedProductIntervalBillingMode,
      false,
      promotionCode
    );
  };

  const renderStatusPaymentErrorModal = () => (
    <ErrorModal
      status={statusPaymentError}
      onRequestClose={() => {
        setStatusPaymentError(0);
      }}
    />
  );

  return (
    <div className="flex justify-center  w-full mt-2 lg:mt-10 bg-md-lightGray">
      <div className="w-full bg-md-white mb-20">
        {renderSpinner()}
        {showPaymentError && renderPaymentError()}
        {showIncompletePaymentRequiresActionErrorModal &&
          renderIncompletePaymentRequiresActionErrorModal()}
        {showIncompletePaymentMethodFailedErrorModal &&
          renderIncompletePaymentMethodFailedErrorModal()}
        {statusPaymentError > 0 && renderStatusPaymentErrorModal()}
        {upgradeCheckoutFailProcessModalIsOpen && renderPaymentUpdateError()}
        {statusUnpaidMeteredError && renderStatusUnpaidMeteredErrorModal()}
        {statusPendingPaymentError && renderStatusPendingPaymentErrorModal()}

        <WaitProcessModal
          timeInMS={NO_CLOSE}
          title={t('Forms.UpgradeCheckout')}
          description={t('Forms.UpgradeCheckoutDescription')}
          isOpen={upgradeCheckoutProcessModalIsOpen}
        />

        <WaitProcessModal
          title={t('Forms.UpgradeCheckoutSuccess')}
          description={t('Forms.UpgradeCheckoutDescription')}
          isOpen={upgradeCheckoutSuccessProcessModalIsOpen}
          timeoutCallback={timeoutCheckoutSuccessWaitProcess}
        />

        {(selectedProduct || demoMode) && phoneValidationNeeded && (
          <PhoneValidationContainerInModal
            isOpen={requestPhoneValidationModalIsOpen}
            mobileToken={mobileToken}
            userData={userData}
            onRequestInfo={onRequestInfo}
            onSubmitFormPhoneValidation={onSubmitFormPhoneValidation}
            onSubmitFormTokenCheck={onDefaultSubmitFormTokenCheck}
            onRequestClose={onRequestPhoneValidationModalClose}
          />
        )}

        {(selectedProduct || demoMode) && !phoneValidationNeeded && (
          <>
            <FormUserAccountInModal
              isOpen={requestUserAccountModalIsOpen}
              actionButton={t('Forms.Checkout.next')}
              countries={countries}
              provinces={provinces}
              tinDocumentTypes={tinDocumentTypes}
              groupId={groupId}
              countryOnChange={countryOnChange}
              showPaymentCheckout={true}
              showAgreementsCheck={isAppSumo(getMe())}
              product={selectedProduct}
              currency={userCurrencySelected}
              onSubmit={onDefaultFormUserAccountSubmit}
              onRequestClose={onRequestPaymentModalClose}
              onCancelButton={onDefaultCancelButton}
              onRequestInfo={onRequestInfo}
              billingMode={selectedProductBillingMode}
            />
          </>
        )}

        <ActivationNeededModal
          isOpen={validationNeededModalIsOpen}
          onRequestClose={onRequestCloseActivationNeededModal}
        />

        <FormContactInModal
          isOpen={requestInfoModalIsOpen}
          shouldCloseOnOverlayClick={true}
          groupId={groupId}
          name={name}
          email={email}
          origin={origin || getOrigin(selectedProduct, selectedProductBillingMode)}
          product={getOrigin(userProductActive?.product)}
          countryId={countryId}
          phone={phone}
          onSubmit={onDefaultRequestInfoSubmit}
          onRequestClose={onRequestInfoClose}
        />

        {confirmNeededPaymentModalIsOpen && oldUserProduct && selectedProduct && (
          <ConfirmNeededPaymentModal
            isOpen={confirmNeededPaymentModalIsOpen}
            onRequestClose={onRequestCloseConfirmNeededPaymentModal}
            onActionButton={onActionConfirmNeededPaymentModal}
            oldUserProduct={oldUserProduct}
            newUserProduct={selectedProduct}
            newCurrency={selectedProductCurrency}
            newIntervalCount={selectedProductIntervalBillingMode}
            isCouponEnabled={COUPONS_ENABLED}
            onConfirmUnpaidMeteredErrorAction={onDefaultConfirmUnpaidMeteredErrorAction}
          />
        )}

        {summaryPaymentModalIsOpen &&
          getCreatePaymentIntent &&
          selectedProduct &&
          invoiceSummaryData && (
            <SummaryPaymentModal
              isOpen={summaryPaymentModalIsOpen}
              onRequestClose={onRequestCloseConfirmNeededPaymentModal}
              onActionButton={onActionConfirmSummaryPaymentModal}
              newUserProduct={selectedProduct}
              newCurrency={selectedProductCurrency}
              newIntervalCount={selectedProductIntervalBillingMode}
              initialInvoiceUpdateSummaryData={invoiceSummaryData}
              isCouponEnabled={COUPONS_ENABLED}
              actionButtonLabel={t('Forms.Checkout.reminder.goToCheckoutShort')}
              getCreatePaymentIntent={getCreatePaymentIntent}
            />
          )}

        <ProductsContainer
          products={products}
          freemiumProduct={freemiumProduct}
          userProducts={userProducts}
          tabSelected={tabSelected}
          setTabSelected={setTabSelected}
          countries={countries}
          defaultSMSCountryProvider={billingDetails?.countryId}
          smsCredits={smsCredits}
          onRequestInfo={onRequestInfo}
          onProductSelect={onDefaultProductSelect}
          onFreeProductSelect={onDefaultFreeProductSelect}
          userIsLogged={userIsLogged()}
        />

        {isEmailMarketingTab(tabSelected) && (
          <div className="flex -mt-28 gap-10 items-center justify-center pb-14">
            <FeatureComparatorLink onComparatorLinkClick={onComparatorLinkClick} />
            {SHOW_TOUR_BUTTON_ON_PRODUCTS && (
              <TourButtonRequest tourRequestURL={TOUR_BUTTON_REQUEST_URL} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
